import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CalendarDays, Clock, ExternalLink, MapPin, Tag } from "lucide-react";
import LazyImage from "../components/common/LazyImage";
import { getEventBySlug } from "../services/api";
import { fallbackEvents } from "../data/eventFallbacks";

const categoryLabels = {
  entertainment: "Entertainment",
  festivals: "Festivals",
  performances: "Performances",
  exhibitions: "Exhibitions",
};

const statusLabels = {
  upcoming: "Upcoming",
  ongoing: "Ongoing",
  past: "Past",
  draft: "Draft",
};

const formatDate = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const formatTime = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
};

const getStatus = (event) => {
  if (event?.status) return event.status;
  if (!event?.event_date) return "past";

  const now = Date.now();
  const start = new Date(event.event_date).getTime();
  const end = event.end_date ? new Date(event.end_date).getTime() : null;

  if (end && start <= now && end > now) return "ongoing";
  return start > now ? "upcoming" : "past";
};

const normalizeEvent = (event) => {
  if (!event) return null;

  return {
    ...event,
    image:
      event.cover_image_url ||
      event.cover_thumbnail_url ||
      event.cover_image_path ||
      event.image ||
      "/images/event-pic.png",
    categoryLabel: categoryLabels[event.category] || event.category || "Event",
    status: getStatus(event),
    gallery: event.gallery || [],
    annual_sponsors: event.annual_sponsors || [],
  };
};

const EventDetail = () => {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let mounted = true;

    const fetchEvent = async () => {
      setLoading(true);
      setNotFound(false);

      try {
        const data = await getEventBySlug(slug);
        if (mounted) setEvent(normalizeEvent(data));
      } catch {
        const fallback = fallbackEvents.find((item) => item.slug === slug);
        if (mounted) {
          setEvent(normalizeEvent(fallback));
          setNotFound(!fallback);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchEvent();
    return () => {
      mounted = false;
    };
  }, [slug]);

  useEffect(() => {
    if (!event?.title) return;

    document.title = `${event.title} - 3E Entertainment`;
    const description = event.description || "Explore this 3E Entertainment event.";
    const metaDescription = document.querySelector('meta[name="description"]');
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDescription = document.querySelector('meta[property="og:description"]');
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');

    metaDescription?.setAttribute("content", description);
    ogTitle?.setAttribute("content", `${event.title} - 3E Entertainment`);
    ogDescription?.setAttribute("content", description);
    twitterTitle?.setAttribute("content", `${event.title} - 3E Entertainment`);
    twitterDescription?.setAttribute("content", description);
  }, [event]);

  const eventMeta = useMemo(() => {
    if (!event) return [];

    return [
      event.event_date && { Icon: CalendarDays, label: "Date", value: formatDate(event.event_date) },
      event.event_date && { Icon: Clock, label: "Time", value: formatTime(event.event_date) },
      (event.venue || event.city || event.state) && {
        Icon: MapPin,
        label: "Location",
        value: [event.venue, event.city, event.state].filter(Boolean).join(", "),
      },
      event.categoryLabel && { Icon: Tag, label: "Category", value: event.categoryLabel },
    ].filter(Boolean);
  }, [event]);

  if (loading) {
    return (
      <div className="event-detail-page">
        <section className="event-detail-loading">
          <span />
          <strong />
          <em />
        </section>
      </div>
    );
  }

  if (notFound || !event) {
    return (
      <div className="event-detail-page">
        <section className="event-not-found">
          <h1>Event not found</h1>
          <p>This event may have been unpublished or moved.</p>
          <Link to="/all-events">Back to all events</Link>
        </section>
      </div>
    );
  }

  return (
    <motion.main
      className="event-detail-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
    >
      <section className="ed-hero">
        <LazyImage src={event.image} alt={event.title} className="ed-hero-image" />
        <div className="ed-hero-shade" />
        <div className="ed-hero-content">
          <Link to="/all-events" className="ed-back-link">
            <ArrowLeft size={16} /> All Events
          </Link>
          <div className="ed-kicker">
            <span>{event.categoryLabel}</span>
            <span className={`ed-status ed-status-${event.status}`}>
              {statusLabels[event.status] || event.status}
            </span>
          </div>
          <h1>{event.title}</h1>
          {event.description && <p>{event.description}</p>}
        </div>
      </section>

      <section className="ed-body">
        <div className="ed-container">
          <aside className="ed-facts">
            {eventMeta.map(({ Icon, label, value }) => (
              <div key={label} className="ed-fact">
                <Icon size={18} />
                <div>
                  <span>{label}</span>
                  <strong>{value}</strong>
                </div>
              </div>
            ))}

            <div className="ed-actions">
              {event.ticket_url && (
                <a href={event.ticket_url} target="_blank" rel="noopener noreferrer" className="ed-primary-btn">
                  Get Tickets <ExternalLink size={15} />
                </a>
              )}
              {event.facebook_album_url && (
                <a href={event.facebook_album_url} target="_blank" rel="noopener noreferrer" className="ed-secondary-btn">
                  View Gallery <ExternalLink size={15} />
                </a>
              )}
            </div>
          </aside>

          <article className="ed-story">
            <h2>About This Event</h2>
            <p>{event.description || "More details for this event will be added soon."}</p>

            {event.annual_sponsors.length > 0 && (
              <div className="ed-sponsors">
                <div className="ed-sponsor-grid">
                  {event.annual_sponsors.map((sponsor) => {
                    const content = (
                      <>
                        {sponsor.logo_path ? (
                          <img src={sponsor.logo_path} alt={`${sponsor.name} logo`} loading="lazy" decoding="async" />
                        ) : (
                          <span className="ed-sponsor-mark">{sponsor.name.slice(0, 1)}</span>
                        )}
                        <div>
                          <strong>{sponsor.name}</strong>
                          {sponsor.tier && <span>{sponsor.tier}</span>}
                        </div>
                      </>
                    );

                    return sponsor.website_url ? (
                      <a
                        key={sponsor.id || sponsor.name}
                        href={sponsor.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ed-sponsor-card"
                      >
                        {content}
                      </a>
                    ) : (
                      <div key={sponsor.id || sponsor.name} className="ed-sponsor-card">
                        {content}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {event.gallery.length > 0 && (
              <div className="ed-gallery">
                <div className="ed-gallery-grid">
                  {event.gallery.map((image) => (
                    <LazyImage
                      key={image.id || image.image_url}
                      src={image.image_url || image.thumbnail_url}
                      alt={image.alt_text || image.title || event.title}
                      className="ed-gallery-image"
                    />
                  ))}
                </div>
              </div>
            )}
          </article>
        </div>
      </section>

      <style>{`
        .event-detail-page { background: #f7f4ef; min-height: 100vh; color: #15120f; }
        .ed-hero {
          position: relative;
          min-height: 72vh;
          display: flex;
          align-items: flex-end;
          overflow: hidden;
          background: #15120f;
        }
        .ed-hero-image {
          position: absolute !important;
          inset: 0;
          width: 100%;
          height: 100%;
        }
        .ed-hero-shade {
          position: absolute;
          inset: 0;
          background:
            linear-gradient(180deg, rgba(10,8,6,0.24) 0%, rgba(10,8,6,0.88) 100%),
            linear-gradient(90deg, rgba(10,8,6,0.82) 0%, rgba(10,8,6,0.18) 72%);
        }
        .ed-hero-content {
          position: relative;
          z-index: 2;
          width: min(1180px, calc(100% - 40px));
          margin: 0 auto;
          padding: 8rem 0 4.5rem;
          color: #fffaf2;
        }
        .ed-back-link {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          color: rgba(255,250,242,0.74);
          text-decoration: none;
          font-weight: 800;
          font-size: 0.85rem;
          margin-bottom: 2rem;
        }
        .ed-kicker {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.75rem;
          margin-bottom: 1.1rem;
          color: rgba(255,250,242,0.78);
          font-size: 0.78rem;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.14em;
        }
        .ed-status {
          border: 1px solid rgba(255,255,255,0.24);
          border-radius: 999px;
          padding: 0.38rem 0.7rem;
          letter-spacing: 0.08em;
        }
        .ed-status-upcoming, .ed-status-ongoing { background: rgba(199,38,49,0.78); color: #fff; }
        .ed-hero h1 {
          max-width: 900px;
          margin: 0;
          font-size: clamp(2.55rem, 7vw, 5.5rem);
          line-height: 0.98;
          font-weight: 950;
          letter-spacing: 0;
        }
        .ed-hero p {
          max-width: 760px;
          margin: 1.5rem 0 0;
          color: rgba(255,250,242,0.78);
          font-size: clamp(1rem, 2vw, 1.18rem);
          line-height: 1.75;
        }
        .ed-body { padding: clamp(3.5rem, 7vw, 6.5rem) 0; }
        .ed-container {
          width: min(1180px, calc(100% - 40px));
          margin: 0 auto;
          display: grid;
          grid-template-columns: minmax(260px, 360px) 1fr;
          gap: clamp(2rem, 6vw, 5rem);
          align-items: start;
        }
        .ed-facts {
          position: sticky;
          top: 110px;
          display: grid;
          gap: 0.85rem;
        }
        .ed-fact {
          display: grid;
          grid-template-columns: 22px 1fr;
          gap: 0.8rem;
          align-items: start;
          background: #fffdf9;
          border: 1px solid rgba(21,18,15,0.08);
          border-radius: 8px;
          padding: 1rem;
        }
        .ed-fact svg { color: #C72631; margin-top: 0.1rem; }
        .ed-fact span {
          display: block;
          margin-bottom: 0.2rem;
          color: rgba(21,18,15,0.48);
          font-size: 0.75rem;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.12em;
        }
        .ed-fact strong { color: #15120f; font-size: 0.95rem; line-height: 1.45; }
        .ed-actions { display: grid; gap: 0.65rem; margin-top: 0.5rem; }
        .ed-primary-btn,
        .ed-secondary-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          min-height: 46px;
          border-radius: 8px;
          text-decoration: none;
          font-weight: 900;
          font-size: 0.86rem;
        }
        .ed-primary-btn { background: #C72631; color: #fff; }
        .ed-secondary-btn { background: #15120f; color: #fffaf2; }
        .ed-story {
          background: #fffdf9;
          border: 1px solid rgba(21,18,15,0.08);
          border-radius: 8px;
          padding: clamp(1.5rem, 4vw, 3rem);
        }
        .ed-story h2 {
          margin: 0 0 1rem;
          font-size: clamp(1.8rem, 4vw, 2.6rem);
          line-height: 1.08;
          letter-spacing: 0;
        }
        .ed-story p {
          margin: 0;
          color: rgba(21,18,15,0.68);
          font-size: 1.02rem;
          line-height: 1.85;
          white-space: pre-line;
        }
        .ed-sponsors { margin-top: 3rem; }
        .ed-sponsor-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
          gap: 0.85rem;
        }
        .ed-sponsor-card {
          min-height: 142px;
          display: grid;
          align-content: center;
          gap: 0.85rem;
          padding: 1rem;
          border: 1px solid rgba(21,18,15,0.08);
          border-radius: 8px;
          background: #fff;
          color: #15120f;
          text-decoration: none;
          transition: transform 0.18s cubic-bezier(0.22, 1, 0.36, 1), border-color 0.18s ease, box-shadow 0.18s ease;
        }
        a.ed-sponsor-card:hover {
          transform: translateY(-2px);
          border-color: rgba(199,38,49,0.24);
          box-shadow: 0 12px 26px rgba(21,18,15,0.08);
        }
        .ed-sponsor-card img {
          width: 100%;
          height: 66px;
          object-fit: contain;
        }
        .ed-sponsor-mark {
          width: 66px;
          height: 66px;
          display: grid;
          place-items: center;
          justify-self: center;
          border-radius: 50%;
          background: #15120f;
          color: #fffaf2 !important;
          font-size: 1.45rem !important;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: 0;
        }
        .ed-sponsor-card div {
          display: grid;
          gap: 0.18rem;
          text-align: center;
        }
        .ed-sponsor-card strong {
          color: #15120f;
          font-size: 0.92rem;
          line-height: 1.25;
        }
        .ed-sponsor-card span {
          color: rgba(21,18,15,0.5);
          font-size: 0.75rem;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }
        .ed-gallery { margin-top: 3rem; }
        .ed-gallery-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 1rem;
        }
        .ed-gallery-image {
          aspect-ratio: 4 / 3;
          border-radius: 8px;
          border: 1px solid rgba(21,18,15,0.08);
        }
        .event-detail-loading,
        .event-not-found {
          min-height: 70vh;
          display: grid;
          place-content: center;
          gap: 1rem;
          text-align: center;
          padding: 7rem 1.25rem;
        }
        .event-detail-loading span,
        .event-detail-loading strong,
        .event-detail-loading em {
          display: block;
          width: min(680px, 82vw);
          height: 18px;
          border-radius: 8px;
          background: #e2ddd5;
          animation: edPulse 1.4s ease-in-out infinite;
        }
        .event-detail-loading strong { height: 52px; }
        .event-detail-loading em { width: min(480px, 64vw); justify-self: center; }
        .event-not-found h1 { margin: 0; font-size: clamp(2rem, 6vw, 3.6rem); }
        .event-not-found p { margin: 0; color: rgba(21,18,15,0.62); }
        .event-not-found a { color: #C72631; font-weight: 900; }
        @keyframes edPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }
        @media (max-width: 860px) {
          .ed-container { grid-template-columns: 1fr; }
          .ed-facts { position: static; }
          .ed-gallery-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 560px) {
          .ed-hero { min-height: 68vh; }
          .ed-hero-content { width: min(100% - 28px, 1180px); padding-bottom: 3rem; }
          .ed-container { width: min(100% - 28px, 1180px); }
        }
      `}</style>
    </motion.main>
  );
};

export default EventDetail;
